import { PrimaryButton } from "@nib-components/button";
import colors from "@nib-components/colors";
import Container from "@nib-components/container";
import Heading from "@nib-components/heading";
import HeroPanel from "@nib-components/hero-panel";
import Section, { SectionBackground } from "@nib-components/section";
import { Box, Inline, Stack } from "@nib/layout";
import { graphql, Link as GatsbyLink, withPrefix } from "gatsby";
import { GatsbySeo, ProductJsonLd } from "gatsby-plugin-next-seo";
import PropTypes from "prop-types";
import React, { createRef } from "react";
import styled from "styled-components";
import breakpoint from "styled-components-breakpoint";
import { Margin, Padding } from "styled-components-spacing";
import { BreadCrumb } from "../../components/BreadCrumb";
import CourseContributors from "../../components/CourseContributors";
import CourseList from "../../components/CourseList/CourseList";
import Layout from "../../components/Layout";
import ProductCTA from "../../components/ProductCTA/ProductCTA";
import ProductHighlights from "../../components/ProductDetails/ProductHighlights";
import EducationFeaturePanel from "../../components/ProductDetails/ProductHighlights/EducationFeaturePanel";
import ProductIntro from "../../components/ProductIntro";
import ProductOverview from "../../components/ProductOverview";
import { StickyFooterCTA } from "../../components/StickyCTA";
import TermsAndConditions from "../../components/TermsAndConditions";
import metrics from "../../metrics";
import useWindowDimensions from "../../utils/breakpoints";
import formatDollars from "../../utils/formatDollars";
import scrollToRef from "../../utils/scrollToRef";

import withQueryParams from "../../utils/withQueryParams";

import { SalesContext } from "../../components/Context/SalesContextProvider";

import Copy from "@nib-components/copy";
import PriceTag from "../../components/PriceTag";
import addScript from "../../hooks/AddScript";

const StyledDiv = styled.div`
  ${breakpoint("md")`
    width: 75%;
    margin: 0 auto;
    padding: 2rem 0;
  `}
`;
const StyledGreyDivider = styled.div`
  display: block;
  height: 30px;
  background-color: ${colors.sneezy};

  ${breakpoint("lg")`
    display: none;
  `};
`;
const ProductTemplatePage = ({ data: { products: product } }) => {
  const salesContext = React.useContext(SalesContext);
  // TODO: remove when campaign is done
  addScript(withPrefix("assets/js/feedback.js"), true);
  const { isMobile } = useWindowDimensions();
  const { content, pricing, title, description, type, image } = product;
  // TODO figure out a better way to do this when we have
  // different product types
  const isEducation = Boolean(type === "education");
  const overview = content.find((value) => value.contentType === "overview");
  const highlight = content.find((value) => value.contentType === "highlight");
  const course = content.find((value) => value.contentType === "courseContent");

  // Refs that we have scrollTo's working at.
  const educationRef = createRef();
  const contributorsRef = createRef();
  const courseContentRef = createRef();

  const paddingHorizontal = {
    xs: 4,
    md: 6,
    lg: 8,
  };
  // Setting sticky footer & start and end Y values
  const highlightsRef = createRef();
  const endRef = createRef();

  return (
    isEducation && (
      <Padding top={{ xs: 4, lg: 8 }}>
        <Padding bottom={{ xs: 4, md: 2 }} horizontal={paddingHorizontal}>
          <Container>
            <BreadCrumb currentPageName={title} socialShare={!salesContext.isMemberCampaign} />
          </Container>
        </Padding>
        <ProductIntro
          content={highlight}
          title={title}
          pricing={pricing}
          description={description}
          image={image}
          onClick={() => scrollToRef(highlightsRef)}
        />
        <div ref={educationRef}>
          <EducationFeaturePanel onClick={() => scrollToRef(contributorsRef)} />
        </div>
        <div ref={highlightsRef}>
          <ProductHighlights
            content={highlight}
            image={image}
            product={product}
            salesContext={salesContext}
          />
        </div>
        <ProductOverview
          content={overview}
          onClick={() => scrollToRef(courseContentRef)}
          image={image}
        />
        <div ref={courseContentRef}>
          <Section background={SectionBackground.white}>
            <Box
              background="white"
              paddingVertical={{ xs: 4, md: 6 }}
              paddingHorizontal={{ xs: 4, md: 10 }}
            >
              <CourseList course={course} />
            </Box>
          </Section>
        </div>
        <StyledGreyDivider />
        <Section>
          <StyledDiv ref={contributorsRef} id="where-from">
            <TermsAndConditions />
            <Margin bottom={{ xs: 8 }} top={6}>
              <CourseContributors />
            </Margin>
          </StyledDiv>
        </Section>
        <ProductCTA title="What happens after I've purchased my course?" />
        <div ref={endRef}>
          <HeroPanel
            title="Start learning today"
            subTitle="Learn at your own pace, in your own time, for as long as you want."
            variation="text"
          >
            <Margin top={{ xs: 7 }}>
              <Stack space={6}>
                <PrimaryButton
                  data-tealium-id="p2p_pregnancy-program_product-page_footer-click"
                  size="large"
                  as={GatsbyLink}
                  to={withQueryParams(`/checkout/`)}
                  state={{ product }}
                >
                  {salesContext.content.button}
                </PrimaryButton>
                {salesContext.isOnSale && (
                  <PriceTag saleCTA={salesContext.saleCTA} align="center" />
                )}
              </Stack>
            </Margin>
          </HeroPanel>
        </div>
        <StickyFooterCTA startYRef={highlightsRef} endYRef={endRef} fullWidth autoHeight>
          {isMobile ? (
            <Box padding={4} display="flex" justifyContent="space-between" alignItems="center">
              {salesContext.isOnSale && (
                <Box width="50%" display="flex" justifyContent="start">
                  {salesContext.saleCTA.split(/\r?\n/).map((text, index) => {
                    return (
                      <Copy key={index} align="left" small>
                        {text}
                      </Copy>
                    );
                  })}
                </Box>
              )}
              <Box
                width={salesContext.isOnSale ? "50%" : "100%"}
                display="flex"
                justifyContent="end"
              >
                <PrimaryButton
                  as={GatsbyLink}
                  to={withQueryParams(`/checkout/`)}
                  state={{ product }}
                  fullWidth
                  align="center"
                  tabIndex={-1}
                >
                  {salesContext.content.button}{" "}
                  {salesContext.paymentRequired &&
                    (salesContext.isOnSale
                      ? salesContext.salesAmount.formatted
                      : formatDollars(pricing.amount))}
                </PrimaryButton>
              </Box>
            </Box>
          ) : (
            <Stack>
              <Inline space={4} verticalAlign="center">
                <Inline space={1} verticalAlign="center">
                  <Heading size={3}>{title} -</Heading>
                  {salesContext.isOnSale && !salesContext.paymentRequired ? (
                    <Heading size={3} style={{ textDecoration: "line-through" }}>
                      {salesContext.defaultAmount.formatted}
                    </Heading>
                  ) : (
                    <Heading size={3}>
                      {salesContext.isOnSale
                        ? salesContext.salesAmount.formatted
                        : formatDollars(pricing.amount)}
                    </Heading>
                  )}
                </Inline>
                <PrimaryButton
                  as={GatsbyLink}
                  to={withQueryParams(`/checkout/`)}
                  state={{ product }}
                  align="center"
                  tabIndex={-1}
                >
                  {salesContext.content.button}
                </PrimaryButton>
              </Inline>
              {salesContext.isOnSale && <PriceTag saleCTA={salesContext.saleCTA} align="center" />}
            </Stack>
          )}
        </StickyFooterCTA>
      </Padding>
    )
  );
};
export default function ProductTemplate(props) {
  const name = props?.data?.products?.slug;
  const Page = metrics({
    pageName: `p2p_pregnancy-program_${name}_product-page`,
  })(ProductTemplatePage);
  const domain = typeof window !== "undefined" ? window.location.hostname : "www.nib.com.au";
  return (
    <Layout
      {...{
        title: `nib's ${props?.data?.products?.name}`,
        description: props?.data?.products?.description?.seo,
      }}
      location={props.location}
    >
      <GatsbySeo
        openGraph={{
          title: `nib's ${props?.data?.products?.name}`,
          description: props?.data?.products?.description?.seo,
          url: `https://${domain}/health-services/nurture/${name}`,
          images: [
            {
              url: `https://${domain}/health-services/nurture/assets/images/og/elearning-product-image.jpg`,
            },
          ],
        }}
        canonical={`https://www.nib.com.au/health-services/nurture/products/${name}`}
      />
      <Page {...props} />
      <ProductJsonLd
        productName={props.data.products.name}
        description={props.data.products.description.long}
        brand="nib"
        offers={{
          price: `${props.data.products.pricing.amount}`,
          priceCurrency: "AUD",
          url: `${props.location.href}`,
        }}
      />
    </Layout>
  );
}

export const pageQuery = graphql`
  query productsBySlug($slug: String!) {
    products(slug: { eq: $slug }) {
      productId
      content {
        contentType
        courseEnhancements {
          details
          name
        }
        courses {
          modules {
            name
            previewLink
            lessons {
              title
            }
          }
          title
          subTitle
          timeToComplete
          moduleId
          image {
            url
            alt
          }
        }
        cta {
          copy
          title
        }
        items
        title
        text
        description
        image {
          url
          alt
        }
      }
      description {
        long
        short
        seo
      }
      pricing {
        amount
      }
      title
      type
      name
      slug
      image {
        url
        alt
      }
    }
  }
`;

ProductTemplatePage.propTypes = {
  data: PropTypes.shape({
    products: PropTypes.shape({
      content: PropTypes.array,
      title: PropTypes.string,
      pricing: PropTypes.shape({
        amount: PropTypes.number,
      }),
      description: PropTypes.shape({
        long: PropTypes.string,
        short: PropTypes.string,
      }),
      type: PropTypes.string,
    }),
  }),
};
